// useBluetooth.ts

import { useRef, useState } from "react";
import { useBluetoothManager } from "./bluetoothContext";
import { bleInfoTable } from "./deviceProfiles";
import { BLUETOOTH_DC_REASONS, BLUETOOTH_STATES } from "./bluetoothConstants";
import { SetupCancelledError } from "./bluetoothManager";
import {
  buildDeviceFilters,
  encodeWifiCredentials,
  parseWifiScanChunks,
} from "./bluetoothProtocol";

// The first chunk read makes the trap run a fresh Wi-Fi scan, which takes up to ~10 s.
const SCAN_CHUNK_READ_TIMEOUT_MS = 20000;
const SCAN_CHUNKS = ["list1", "list2", "list3", "list4", "list5"];

// The trap reports one final "active" result per connect attempt. Older firmware can report false
// after its short internet check while NetworkManager is still joining, then true once the trap is
// online, so a false only counts as a failure when no true follows within this window.
export const WIFI_CONNECT_FAILURE_GRACE_MS = 15000;
// Backstop for a trap that never reports a result.
export const WIFI_CONNECT_TIMEOUT_MS = 120000;

// Must hold for every firmware in the field. Current firmware removes a failed network and the
// trap rejoins its previous one, but older firmware (process manager v2.4-v2.9) stays on a network
// that joined without internet, so the message cannot promise the trap goes back.
export const wifiConnectFailedMessage = (ssid: string) =>
  `Could not get the trap online through ${ssid}. Check the password and that the network has internet. If the trap does not reconnect to its previous network within a minute, connect to it again here and choose a working network.`;

export function useBluetooth({
  handleSendErrorMessage,
}: {
  handleSendErrorMessage: (msg: string) => void;
}) {
  const manager = useBluetoothManager();

  const [connected, setConnected] = useState(false);
  const [isWifiConnected, _setIsWifiConnected] = useState(false);
  const [currentNetwork, setCurrentNetwork] = useState("");
  // Counts failed connect attempts so the setup screen can drop the network it was waiting for.
  const [wifiConnectFailures, setWifiConnectFailures] = useState(0);
  const [bluetoothState, _setBluetoothState] = useState(
    BLUETOOTH_STATES.DISCONNECTED,
  );
  const bluetoothStateRef = useRef<string>(BLUETOOTH_STATES.DISCONNECTED);
  // Mirrors isWifiConnected for the 2 minute disconnect timeout, whose closure would otherwise
  // see the value from when it was scheduled.
  const isWifiConnectedRef = useRef<boolean>(false);
  const wifiConnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wifiConnectGraceRef = useRef<NodeJS.Timeout | null>(null);
  const wifiDisconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wifiTargetRef = useRef<string>("");
  const wifiConnectAttemptRef = useRef(0);
  const wIsDisconnectingRef = useRef<boolean>(false);

  const setBluetoothState = (state: string) => {
    _setBluetoothState(state);
    bluetoothStateRef.current = state;
  };

  const setIsWifiConnected = (value: boolean) => {
    _setIsWifiConnected(value);
    isWifiConnectedRef.current = value;
  };

  const clearWifiConnectTimers = () => {
    if (wifiConnectTimeoutRef.current) {
      clearTimeout(wifiConnectTimeoutRef.current);
      wifiConnectTimeoutRef.current = null;
    }
    if (wifiConnectGraceRef.current) {
      clearTimeout(wifiConnectGraceRef.current);
      wifiConnectGraceRef.current = null;
    }
  };

  const failWifiConnect = () => {
    const target = wifiTargetRef.current;
    clearWifiConnectTimers();
    console.warn(`Wifi connect to ${target} failed, resetting wifi state...`);
    handleSendErrorMessage(wifiConnectFailedMessage(target));
    setBluetoothState(BLUETOOTH_STATES.CONNECTED);
    wifiTargetRef.current = "";
    setWifiConnectFailures((count) => count + 1);
  };

  async function connect(deviceUuid: string = "") {
    try {
      setBluetoothState(BLUETOOTH_STATES.CONNECTING);
      if (!manager.checkSupportBLE()) {
        setBluetoothState(BLUETOOTH_STATES.UNSUPPORTED);
        return false;
      }

      manager.setOnDisconnectCallback((reason: number) => {
        clearWifiConnectTimers();
        setConnected(false);
        setBluetoothState(BLUETOOTH_STATES.DISCONNECTED);
        if (reason !== BLUETOOTH_DC_REASONS.NONE) {
          console.error("Disconnected due to an error");
          let errorMsg;
          switch (reason) {
            case BLUETOOTH_DC_REASONS.DEVICE_RESTART:
              errorMsg = `Bluetooth connection was lost, possibly due to a device restart. Please refresh the page and try again.`;
              break;
            case BLUETOOTH_DC_REASONS.SETUP:
              errorMsg = `Bluetooth connection was lost during setup. Please refresh the page and try again.`;
              break;
            default:
              errorMsg = `Bluetooth connection was lost. Please refresh the page and try again.`;
              break;
          }
          handleSendErrorMessage(errorMsg);
        }
      });
      await manager.requestDevice(
        bleInfoTable.services,
        buildDeviceFilters(deviceUuid),
      );

      await manager.connectAndSetup(
        bleInfoTable.services,
        bleInfoTable.characteristics,
      );

      console.log("Connected to Bluetooth device, clearing wifi state...");
      manager.readBool("scan", "active").then((isActive) => {
        console.log(`Initial wifi active status: ${isActive}`);
        setIsWifiConnected(isActive);
      });

      console.log("Subscribing to Bluetooth notifications...");
      manager.subscribe("scan", "active", async (value: DataView) => {
        const bytes = new Uint8Array(value.buffer);
        console.log(
          `Received wifi active status: ${bytes[0]} for bluetooth state ${bluetoothStateRef.current}`,
        );
        const isActive = bytes[0] === 1 ? true : false;

        try {
          setIsWifiConnected(isActive);
          if (!isActive) {
            setCurrentNetwork("");
          }
          if (bluetoothStateRef.current === BLUETOOTH_STATES.WIFI_CONNECTING) {
            console.log(
              `Wifi got connected successfully, wIsDisconnectingRef.current: ${wIsDisconnectingRef.current}; isActive: ${isActive}; bluetoothStateRef.current: ${bluetoothStateRef.current}; wifiTargetRef.current: ${wifiTargetRef.current}`,
            );
            if (!wIsDisconnectingRef.current && !isActive) {
              if (!wifiConnectGraceRef.current) {
                wifiConnectGraceRef.current = setTimeout(() => {
                  wifiConnectGraceRef.current = null;
                  if (
                    bluetoothStateRef.current ===
                    BLUETOOTH_STATES.WIFI_CONNECTING
                  ) {
                    failWifiConnect();
                  }
                }, WIFI_CONNECT_FAILURE_GRACE_MS);
              }
            } else if (!wIsDisconnectingRef.current && isActive) {
              if (wifiConnectGraceRef.current) {
                clearTimeout(wifiConnectGraceRef.current);
                wifiConnectGraceRef.current = null;
              }
              // possibly successful connection
              // first check wifiName to make sure the wifi was updated to the right ssid
              let wifiName = "";
              for (let i = 0; i < 10; i++) {
                try {
                  wifiName = await manager.read("connect", "name", 10000);
                } catch (error) {
                  await new Promise((r) => setTimeout(r, 1000));
                  continue;
                }
                break;
              }
              console.log(
                `Got wifi name ${wifiName} after connection status update`,
              );
              if (wifiName === wifiTargetRef.current) {
                clearWifiConnectTimers();
                setCurrentNetwork(wifiName);
                setBluetoothState(BLUETOOTH_STATES.CONNECTED);
                wifiTargetRef.current = "";
              }
            } else if (wIsDisconnectingRef.current && !isActive) {
              console.log(
                `Wifi disconnected successfully, wIsDisconnectingRef.current: ${wIsDisconnectingRef.current}; isActive: ${isActive}; bluetoothStateRef.current: ${bluetoothStateRef.current}; wifiTargetRef.current: ${wifiTargetRef.current}`,
              );
              // successful disconnection
              setCurrentNetwork("");
              if (wifiDisconnectTimeoutRef.current) {
                clearTimeout(wifiDisconnectTimeoutRef.current);
                wifiDisconnectTimeoutRef.current = null;
              }
              setBluetoothState(BLUETOOTH_STATES.CONNECTED);
            }
          }
        } catch (error) {
          const errorStr =
            error instanceof Error ? error.message : String(error);
          if (errorStr.includes("GATT Service no longer exists")) {
            manager.disconnect();
          }
        }
      });

      setBluetoothState(BLUETOOTH_STATES.CONNECTED);
      setConnected(true);
    } catch (error) {
      // disconnect() already reset everything, and a newer pairing may have started since.
      if (error instanceof SetupCancelledError) {
        return true;
      }
      console.error(`Connect Error: ${error}`);
      const errorStr = error instanceof Error ? error.message : String(error);
      setBluetoothState(BLUETOOTH_STATES.DISCONNECTED);
      setConnected(false);
      manager.disconnect(BLUETOOTH_DC_REASONS.NONE);
      if (errorStr.includes("User cancelled")) {
        return true;
      }
      return false;
    }
    return true;
  }

  // Resolves to null when the list could not be read, and to the (possibly empty) list otherwise.
  async function scanWifi(): Promise<string[] | null> {
    setBluetoothState(BLUETOOTH_STATES.SCANNING);
    try {
      // One read at a time: Chrome on Android does not queue GATT operations across
      // characteristics, so parallel reads fail with all but one still in flight.
      const chunks: string[] = [];
      for (const chunk of SCAN_CHUNKS) {
        chunks.push(
          await manager.read("scan", chunk, SCAN_CHUNK_READ_TIMEOUT_MS),
        );
      }
      const { ssids, activeNetwork } = parseWifiScanChunks(chunks);
      if (activeNetwork) {
        setCurrentNetwork(activeNetwork);
      }
      setBluetoothState(BLUETOOTH_STATES.CONNECTED);
      return ssids;
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      console.error(`Scan Wifi Error: ${error}`);
      if (errorStr.includes("GATT Service no longer exists")) {
        manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
      } else if (bluetoothStateRef.current === BLUETOOTH_STATES.SCANNING) {
        setBluetoothState(BLUETOOTH_STATES.CONNECTED);
      }
      return null;
    }
  }

  async function startWifiDisconnect(ssid: string = "") {
    setBluetoothState(BLUETOOTH_STATES.WIFI_CONNECTING);
    wIsDisconnectingRef.current = true;

    try {
      await manager.writeStr("connect", "disconnect", ssid);
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      if (errorStr.includes("GATT Service no longer exists")) {
        manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
      } else {
        console.log(`Error writing wifi disconnect: ${error}`);
      }
    }

    // timeout after 2 minutes to reset wifi state if connection fails, since we won't receive a notification from the device in that case
    if (wifiDisconnectTimeoutRef.current) {
      clearTimeout(wifiDisconnectTimeoutRef.current);
    }
    wifiDisconnectTimeoutRef.current = setTimeout(async () => {
      if (isWifiConnectedRef.current) {
        handleSendErrorMessage(
          `There was an error disconnecting from wifi. Please refresh the page and try again.`,
        );
        console.warn("Wifi connect timeout, resetting wifi state...");
      }
      setBluetoothState(BLUETOOTH_STATES.CONNECTED);
    }, 120000);
  }

  // Switching networks writes the new credentials without disconnecting first: the trap moves to
  // the new network only once it is joined, and a failed attempt leaves the previous network
  // saved for it to fall back to.
  async function startWifiConnect(ssid: string, psk: string) {
    const attempt = ++wifiConnectAttemptRef.current;
    clearWifiConnectTimers();
    setBluetoothState(BLUETOOTH_STATES.WIFI_CONNECTING);
    wIsDisconnectingRef.current = false;

    wifiTargetRef.current = ssid;

    try {
      // The trap acknowledges the write straight away and reports the outcome through the
      // "active" notification. Older firmware holds the write open for the whole attempt, so a
      // timeout here does not mean the connection failed.
      await manager.writeStr(
        "connect",
        "wpa",
        encodeWifiCredentials(ssid, psk),
      );
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      if (errorStr.includes("GATT Service no longer exists")) {
        manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
      }
      console.log(`Error writing wifi connect info: ${error}`);
    }

    if (
      attempt !== wifiConnectAttemptRef.current ||
      wifiTargetRef.current !== ssid
    ) {
      // Settled, or replaced by a newer attempt, while the write was in flight.
      return;
    }
    wifiConnectTimeoutRef.current = setTimeout(async () => {
      wifiConnectTimeoutRef.current = null;
      let wifiName = "";
      let online = false;
      try {
        wifiName = await manager.read("connect", "name", 10000);
        online = await manager.readBool("scan", "active", 10000);
        setIsWifiConnected(online);
      } catch (error) {
        const errorStr = error instanceof Error ? error.message : String(error);
        if (errorStr.includes("GATT Service no longer exists")) {
          manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
        }
        console.log(
          `Error reading wifi state after wifi connect timeout: ${error}`,
        );
      }
      if (bluetoothStateRef.current !== BLUETOOTH_STATES.WIFI_CONNECTING) {
        return;
      }

      // The "active" notification can arrive before the name characteristic catches up, in
      // which case only this check sees the trap on the target network. Joining is not enough:
      // the trap has to be online, or it stays dark once the dashboard is closed.
      if (online && wifiName === wifiTargetRef.current) {
        clearWifiConnectTimers();
        setBluetoothState(BLUETOOTH_STATES.CONNECTED);
        setCurrentNetwork(wifiName);
        wifiTargetRef.current = "";
        return;
      }
      setCurrentNetwork(online ? wifiName : "");
      failWifiConnect();
    }, WIFI_CONNECT_TIMEOUT_MS);
  }

  function disconnect() {
    clearWifiConnectTimers();
    wifiTargetRef.current = "";
    manager.disconnect(BLUETOOTH_DC_REASONS.NONE);
    setConnected(false);
    setIsWifiConnected(false);
    setCurrentNetwork("");
    setBluetoothState(BLUETOOTH_STATES.DISCONNECTED);
  }

  return {
    connected,
    connect,
    disconnect,
    bluetoothState,
    scanWifi,
    startWifiConnect,
    startWifiDisconnect,
    isWifiConnected,
    currentNetwork,
    wifiConnectFailures,
  };
}
